/**
 * MyReports — „Moje zgłoszenia" (V2 2026-05-18).
 *
 * Lista zgłoszeń usera + detail + komentarze + potwierdź/odrzuć fix.
 * WebSocket live: status push na kanale "reports" (bez pollingu).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Bug, RefreshCw, Loader2, CheckCircle2, XCircle, MessageSquare, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getMyUserReports, getMyUserReportDetail, addMyUserReportComment,
  confirmMyUserReportFix, rejectMyUserReportFix,
} from '../lib/api';

const STATUS_META = {
  open:                       { label: 'Otwarte',          color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  in_progress:                { label: 'W trakcie',        color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  awaiting_user_confirmation: { label: 'Czeka na Ciebie',  color: 'bg-purple-500/15 text-purple-300 border-purple-500/40' },
  resolved:                   { label: 'Rozwiązane',       color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  wontfix:                    { label: 'Nie naprawimy',    color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
  duplicate:                  { label: 'Duplikat',         color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
};
const SEV = { low: 'Niski', medium: 'Średni', high: 'Wysoki', critical: 'Krytyczny' };

function fmt(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(s).slice(0, 16); }
}

function ReportDetail({ id, onChanged }) {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setR(await getMyUserReportDetail(id)); }
    catch { toast.error('Nie udało się załadować zgłoszenia'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!r) return null;

  const awaiting = r.status === 'awaiting_user_confirmation';

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Błąd'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 border-t border-border pt-3 mt-2">
      <div className="text-xs text-muted-foreground">{r.description}</div>
      <div className="flex gap-3 text-[11px] text-muted-foreground flex-wrap">
        <span>Moduł: {r.module}</span>
        <span>Priorytet: {SEV[r.severity] || r.severity}</span>
        <span>Zgłoszono: {fmt(r.created_at)}</span>
        {r.resolved_at && <span>Rozwiązano: {fmt(r.resolved_at)}</span>}
      </div>

      {(r.comments || []).length > 0 && (
        <div className="space-y-1.5">
          {r.comments.map((c, i) => (
            <div key={i} className={`text-xs rounded p-2 ${c.by === 'user' ? 'bg-muted/40' : 'bg-blue-500/10'}`}>
              <span className="font-medium">{c.by === 'user' ? 'Ty' : 'Zespół'}</span>
              <span className="text-muted-foreground"> · {fmt(c.at)}</span>
              <div className="mt-0.5">{c.text}</div>
            </div>
          ))}
        </div>
      )}

      {awaiting && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3 space-y-2">
          <p className="text-xs font-medium">Zgłoszenie zostało naprawione — czy działa?</p>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy}
              onClick={() => act(() => confirmMyUserReportFix(id), 'Dziękujemy za potwierdzenie')}
              className="gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Tak, działa
            </Button>
          </div>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Jeśli nadal nie działa — opisz co dokładnie..." rows={2} className="text-xs" />
          <Button size="sm" variant="outline" disabled={busy || !rejectReason.trim()}
            onClick={() => act(() => rejectMyUserReportFix(id, rejectReason), 'Zgłoszenie ponownie otwarte')}
            className="gap-1">
            <XCircle className="w-3.5 h-3.5" /> Nadal nie działa
          </Button>
        </div>
      )}

      {!['resolved'].includes(r.status) && (
        <div className="space-y-1.5">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Dodaj komentarz / więcej szczegółów..." rows={2} className="text-xs" />
          <Button size="sm" variant="outline" disabled={busy || !comment.trim()}
            onClick={() => act(async () => { await addMyUserReportComment(id, comment); setComment(''); }, 'Komentarz dodany')}
            className="gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> Wyślij komentarz
          </Button>
        </div>
      )}
    </div>
  );
}

export default function MyReports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [params] = useSearchParams();
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getMyUserReports(100); setItems(r.items || []); }
    catch { toast.error('Nie udało się załadować zgłoszeń'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-expand z ?id= (link z notyfikacji)
  useEffect(() => {
    const qid = params.get('id');
    if (qid) setExpanded(qid);
  }, [params]);

  // WebSocket live — kanał "reports"
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.channel === 'reports' && m.payload) {
            toast.info(`Status zgłoszenia: ${STATUS_META[m.payload.status]?.label || m.payload.status}`);
            load();
          }
        } catch { /* */ }
      };
      ws.onerror = () => {};
      return () => { try { ws.close(); } catch { /* */ } };
    } catch { return undefined; }
  }, [load]);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bug className="w-6 h-6 text-primary" /> Moje zgłoszenia
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Status zgłoszonych problemów. Gdy coś naprawimy — poprosimy Cię o potwierdzenie.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Odśwież
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <Bug className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Brak zgłoszeń.</p>
          <p className="text-xs mt-1">Użyj przycisku 🐛 w prawym dolnym rogu by zgłosić problem.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(r => {
            const st = STATUS_META[r.status] || STATUS_META.open;
            const isOpen = expanded === r.id;
            return (
              <Card key={r.id} className={r.status === 'awaiting_user_confirmation' ? 'border-purple-500/40' : ''}>
                <CardContent className="p-3">
                  <button className="w-full text-left" onClick={() => setExpanded(isOpen ? null : r.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                          <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                          {r.severity === 'critical' && (
                            <Badge className="text-[10px] bg-red-500/15 text-red-300 border-red-500/30">krytyczny</Badge>
                          )}
                          <span className="text-[11px] text-muted-foreground">{fmt(r.created_at)}</span>
                        </div>
                        <div className="font-medium text-sm mt-1">{r.title}</div>
                      </div>
                      <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {isOpen && <ReportDetail id={r.id} onChanged={load} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
